import { useState, useEffect } from 'react';
import { imageRegistry } from '@config/imageRegistry';

interface ImageComponentProps {
  src: string;
  alt: string;
  category: string;
  priority?: number;
  className?: string;
  onClick?: () => void;
  // New optional accessibility/SEO props
  decorative?: boolean; // if true, hide from assistive tech
  sizes?: string;
  width?: number | string;
  height?: number | string;
  fetchPriority?: 'high' | 'low' | 'auto';
  loading?: 'lazy' | 'eager';
  decoding?: 'async' | 'sync' | 'auto';
}

const ImageComponent = ({ 
  src, 
  alt, 
  category, 
  priority = 0, 
  className = '', 
  onClick,
  decorative = false,
  sizes,
  width,
  height,
  fetchPriority = 'auto',
  loading,
  decoding = 'async',
}: ImageComponentProps) => {
  const [loadingState, setLoadingState] = useState(true);
  const [error, setError] = useState(false);
  const [imageSrc, setImageSrc] = useState(src);

  useEffect(() => {
    // Check if the image exists in the registry with a different source
    const registryEntry = imageRegistry.find(
      img => img.id === src || img.src === src || img.altSources?.includes(src)
    );

    if (registryEntry) {
      // Use the primary source from the registry
      setImageSrc(registryEntry.src);
    } else {
      setImageSrc(src);
    }

    const img = new Image();
    img.src = registryEntry?.src || src;
    
    img.onload = () => {
      setLoadingState(false);
    };
    
    img.onerror = () => {
      setError(true);
      setLoadingState(false);
      
      // Try fallback image if available in registry
      if (registryEntry && registryEntry.fallbackSrc) {
        setImageSrc(registryEntry.fallbackSrc);
      }
    };
    
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [src]);

  // Derive a reasonable alt if none provided and not decorative
  const deriveAltFromSrc = (path: string) => {
    try {
      const file = path.split('/').pop() || '';
      const name = file.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ').trim();
      return name ? name.charAt(0).toUpperCase() + name.slice(1) : 'Image';
    } catch {
      return 'Image';
    }
  };

  const computedAlt = decorative ? '' : (alt || deriveAltFromSrc(imageSrc));

  if (loadingState) {
    return (
      <div 
        className={`bg-secondary-light animate-pulse ${className}`}
        aria-label="Loading image..."
      />
    );
  }

  if (error) {
    return (
      <div 
        className={`bg-secondary-light flex items-center justify-center ${className}`}
        role={decorative ? undefined : 'img'}
        aria-hidden={decorative || undefined}
        aria-label={decorative ? undefined : computedAlt}
      >
        <svg 
          className="w-8 h-8 text-secondary" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" 
          />
        </svg>
      </div>
    );
  }

  // If fetchPriority is high, prefer eager loading for LCP images
  const loadingAttr = fetchPriority === 'high' ? 'eager' : (loading || 'lazy');

  return (
    <img
      src={imageSrc}
      alt={computedAlt}
      className={className}
      loading={loadingAttr}
      decoding={decoding}
      // @ts-ignore fetchPriority is supported in modern browsers
      fetchpriority={fetchPriority}
      onClick={onClick}
      data-category={category}
      data-priority={priority}
      sizes={sizes}
      width={width}
      height={height}
      aria-hidden={decorative || undefined}
      role={decorative ? 'presentation' : undefined}
    />
  );
};

export default ImageComponent;